import React, { useEffect, useRef } from 'react';
import './DitherGif.css';

interface DitherGifProps {
  // An MP4/WebM, not a .gif — see the comment below for why.
  src: string;
  // Dark-to-bright ramp of [r, g, b] (0..1) tones the dithered levels are
  // quantized into, instead of a single hue faded to black — more stops
  // reads as more distinct color, not just more gray steps.
  palette?: [number, number, number][];
  pixelSize?: number;
  // Extra class on the outer frame — the component itself doesn't force
  // an aspect ratio (see DitherGif.css), so callers size it via their own
  // wrapper/class, same as a plain <video>.
  className?: string;
  // Gates both video playback and the draw loop — not just visual
  // opacity. Several of these can be mounted at once (e.g. one per
  // HomeV2 nav item, eagerly warmed so there's no decode delay on first
  // hover); without this every mounted instance keeps decoding video and
  // running getImageData every rAF tick forever, whether or not it's
  // ever visible. Default true so a single standalone usage (e.g. the
  // demo page) doesn't need to pass it.
  active?: boolean;
  // Mirrors the sampled frame horizontally before dithering — a canvas
  // transform on the sample step, not a CSS transform on the <video>
  // (that wouldn't affect what drawImage() reads into the canvas).
  flip?: boolean;
  // HTMLMediaElement.playbackRate — 1 is native speed, <1 slows it down.
  speed?: number;
  // Vertical anchor for the cover-crop, 0 (top of source) to 1 (bottom),
  // like CSS object-position's Y axis — only matters when the crop has
  // to cut height (source taller relative to the canvas than the canvas
  // itself). Default 0.5 keeps the previous centered crop.
  focalY?: number;
}

// 4x4 Bayer ordered-dither matrix, normalized to 0..1 — identical to the
// one in Dither.tsx, reused here against real sampled pixels instead of
// procedural Perlin noise.
const BAYER_4X4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map(v => v / 16);

// Ember-to-flame ramp: darkest tone is still a deep red-orange (not
// black), brightest is a hot, saturated amber-orange highlight — more
// and brighter orange tones than a single hue linearly faded to black.
const FIRE_PALETTE: [number, number, number][] = [
  [0.12, 0.02, 0.0],
  [0.32, 0.06, 0.0],
  [0.55, 0.12, 0.0],
  [0.78, 0.22, 0.02],
  [0.95, 0.35, 0.04],
  [1.0, 0.5, 0.08],
  [1.0, 0.65, 0.18],
  [1.0, 0.8, 0.35],
];

/*
 * Same ordered-dither look as Dither.tsx, but the source signal is a real
 * clip's current frame instead of generated noise.
 *
 * This started as an animated GIF played through a hidden <img>, sampled
 * every requestAnimationFrame via drawImage(). That reliably froze on
 * frame 1 in testing regardless of visibility/opacity/sizing tricks —
 * Chromium's GIF frame-advance timer is independent of both rAF and the
 * image's own paint/compositing state, and offscreen <img> GIFs are not
 * guaranteed to keep ticking. Converting the source clip to video (see
 * ffmpeg conversion at asset prep time) sidesteps this entirely: video
 * playback runs on the media pipeline, not the image-decode timer, so a
 * hidden/offscreen <video loop muted>, played imperatively (see `active`
 * below), keeps advancing frames
 * reliably and drawImage(videoEl, ...) is the standard way to sample it.
 */
export default function DitherGif({
  src,
  palette = FIRE_PALETTE,
  pixelSize = 6,
  className = '',
  active = true,
  flip = false,
  speed = 1,
  focalY = 0.5,
}: DitherGifProps) {
  const colorNum = palette.length;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let width = 1;
    let height = 1;
    let dpr = 1;
    let cols = 1;
    let rows = 1;

    // Offscreen sample canvas: one pixel read per dither cell, not per
    // screen pixel — sampling at full resolution every frame would be
    // far more getImageData work than the effect needs.
    const sampleCanvas = document.createElement('canvas');
    const sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, Math.floor(rect.width * dpr));
      height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.width = width;
      canvas.height = height;

      const cell = Math.max(2, pixelSize * dpr);
      cols = Math.ceil(width / cell);
      rows = Math.ceil(height / cell);
      sampleCanvas.width = cols;
      sampleCanvas.height = rows;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // Paused (not torn down) while inactive: video.currentTime and the
    // canvas's last-drawn frame both stay put, so reactivating just
    // resumes decoding/drawing from where it left off — no re-seek, no
    // blank first frame, same "already warm" guarantee as before.
    if (!active) {
      video.pause();
      return () => { ro.disconnect(); };
    }

    video.playbackRate = speed;
    video.play().catch(() => {});

    const draw = () => {
      if (sampleCtx && video.readyState >= video.HAVE_CURRENT_DATA && video.videoWidth) {
        // object-fit: cover, done manually — this frame isn't always the
        // clip's own aspect ratio (e.g. HomeV2's hover ribbon is far
        // wider/shorter than 16:9), so crop the source rect to match the
        // canvas's aspect before the downscale, rather than stretching.
        const srcAspect = video.videoWidth / video.videoHeight;
        const dstAspect = cols / rows;
        let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;
        if (srcAspect > dstAspect) {
          sw = video.videoHeight * dstAspect;
          sx = (video.videoWidth - sw) / 2;
        } else {
          sh = video.videoWidth / dstAspect;
          sy = (video.videoHeight - sh) * focalY;
        }

        // Downscale the cropped current frame straight to cols x rows —
        // the browser's own image smoothing does the box-filter/average
        // that a per-cell luminance read wants, in one drawImage call.
        // flip mirrors the sample canvas itself (not the source rect),
        // so it works the same regardless of the cover-crop above.
        sampleCtx.save();
        if (flip) {
          sampleCtx.translate(cols, 0);
          sampleCtx.scale(-1, 1);
        }
        sampleCtx.drawImage(video, sx, sy, sw, sh, 0, 0, cols, rows);
        sampleCtx.restore();
        const { data } = sampleCtx.getImageData(0, 0, cols, rows);

        const cell = Math.max(2, pixelSize * dpr);
        for (let cy = 0; cy < rows; cy++) {
          for (let cx = 0; cx < cols; cx++) {
            const i = (cy * cols + cx) * 4;
            const lum = (0.3 * data[i] + 0.6 * data[i + 1] + 0.1 * data[i + 2]) / 255;

            const bx = cx % 4;
            const by = cy % 4;
            const threshold = BAYER_4X4[by * 4 + bx] - 0.25;
            const step = 1 / (colorNum - 1);
            let q = lum + threshold * step;
            q = Math.max(0, Math.min(1, q));
            const index = Math.round(q * (colorNum - 1));

            const [pr, pg, pb] = palette[index];
            ctx.fillStyle = `rgb(${Math.round(pr * 255)}, ${Math.round(pg * 255)}, ${Math.round(pb * 255)})`;
            ctx.fillRect(cx * cell, cy * cell, cell, cell);
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [palette, colorNum, pixelSize, active, flip, speed, focalY]);

  return (
    <div className={`dither-gif-frame ${className}`}>
      <video
        ref={videoRef}
        src={src}
        className="dither-gif-source"
        loop
        muted
        playsInline
        preload="auto"
      />
      <canvas ref={canvasRef} className="dither-gif-canvas" />
    </div>
  );
}
